import { useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { INPUT_COLOR_BORDER, BOX_SHADOW_FIELDS } from "../../utils/colors";
import { BORDER_RADIUS } from "../../utils/constants";
import { getReactHookFormNestedError } from "../../utils/errors";

const COLOR_DRAG_THROTTLE_MS = 100;

const ColorField = ({
  control,
  name = "",
  label = "",
  errors = {},
  rules = {},
  fullWidth = false,
  size = "small",
  disabled = false,
  trailing = null,
  actionsChange = () => {},
}) => {
  const error = getReactHookFormNestedError(errors, name);
  const throttleRef = useRef({});
  const [liveValue, setLiveValue] = useState(null);

  const getThrottleState = () => {
    if (!throttleRef.current[name]) {
      throttleRef.current[name] = { last: 0, pending: null, timeout: null };
    }
    return throttleRef.current[name];
  };

  const sxTextField = {
    "& .MuiInputBase-root": {
      backgroundColor: "fields.background",
      borderRadius: BORDER_RADIUS,
      color: "#000 !important",
      border: `1px solid ${INPUT_COLOR_BORDER}`,
      boxShadow: BOX_SHADOW_FIELDS,
      "& input": {
        color: "#000 !important",
        fontFamily: "monospace",
        fontSize: "0.875rem",
      },
    },
  };

  if (!control) {
    return null;
  }

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => {
        const emitChange = (value) => {
          field.onChange(value);
          actionsChange(value);
        };
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {label && (
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {label}
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                width: "100%",
              }}
            >
              <input
                type="color"
                value={(liveValue ?? field.value) || "#000000"}
                onChange={(e) => {
                  const value = e.target.value;
                  const state = getThrottleState();
                  state.pending = value;
                  const now = Date.now();
                  if (now - state.last >= COLOR_DRAG_THROTTLE_MS) {
                    state.last = now;
                    state.pending = null;
                    setLiveValue(value);
                    emitChange(value);
                  } else if (!state.timeout) {
                    state.timeout = setTimeout(
                      () => {
                        state.timeout = null;
                        state.last = Date.now();
                        if (state.pending != null) {
                          const v = state.pending;
                          state.pending = null;
                          setLiveValue(v);
                          emitChange(v);
                        }
                      },
                      COLOR_DRAG_THROTTLE_MS - (now - state.last)
                    );
                  }
                }}
                onBlur={(e) => {
                  const state = getThrottleState();
                  if (state.timeout) {
                    clearTimeout(state.timeout);
                    state.timeout = null;
                  }
                  if (state.pending != null) {
                    emitChange(state.pending);
                    state.pending = null;
                  }
                  setLiveValue(null);
                  field.onBlur(e);
                }}
                disabled={disabled}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: BORDER_RADIUS,
                  cursor: disabled ? "not-allowed" : "pointer",
                  border: "none",
                  backgroundColor: "transparent",
                  padding: 0,
                }}
              />
              <TextField
                value={(liveValue ?? field.value) || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setLiveValue(value);
                  emitChange(value);
                }}
                onBlur={(e) => {
                  setLiveValue(null);
                  field.onBlur(e);
                }}
                size={size}
                fullWidth={!trailing && fullWidth}
                disabled={disabled}
                error={!!error}
                sx={{
                  ...sxTextField,
                  ...(trailing
                    ? {
                        flex: 1,
                        minWidth: 0,
                      }
                    : { flex: fullWidth ? 1 : undefined, minWidth: 0 }),
                }}
              />
              {trailing}
            </Box>
            {error?.message ? (
              <Typography variant="caption" color="error">
                {error.message}
              </Typography>
            ) : null}
          </Box>
        );
      }}
    />
  );
};

export default ColorField;
